import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const VIBER_INVITE_URL = 'https://invite.viber.com/?g2=AQAP2SKFNmnwWFYyTo8QglRVCrGwCxn5%2F3aimX7%2BTJXhQ4cJgW1zvwlpdc3KAQ%2Fm';
const DISMISS_KEY = 'viber_popup_dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // Show again after 24 hours

const ViberPopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      if (elapsed < DISMISS_DURATION_MS) return;
    }
    const timer = setTimeout(() => setIsVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleDismiss}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Popup */}
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 z-10 bg-white/20 hover:bg-white/40 rounded-full p-1.5 transition-colors"
        >
          <XMarkIcon className="h-5 w-5 text-white" />
        </button>

        {/* Purple gradient background */}
        <div className="bg-gradient-to-b from-purple-600 via-purple-700 to-purple-800 px-6 pt-8 pb-6 text-center">
          {/* Viber icon */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <svg viewBox="0 0 48 48" className="h-12 w-12" fill="none">
              <circle cx="24" cy="24" r="24" fill="white" fillOpacity="0.15" />
              <path d="M35.2 12.8C33.4 11 28.8 8.6 24.2 8.4h-.1c-4.6.2-9.2 2.6-11 4.4-1.4 1.4-2.8 3.8-3.2 7.2-.4 3.4-.1 7.4 2.2 10.4v4.4c0 .6.4 1.2 1 1.4.2.1.4.1.6.1.4 0 .8-.2 1-.4l2.8-2.8c1.2.3 2.4.4 3.6.4h.1c4.6-.2 9.2-2.6 11-4.4 1.4-1.4 2.8-3.8 3.2-7.2.4-3.4.1-7.4-2.2-10.1zM27 19.6c.2.2.2.6 0 .8l-1.6 1.6c-.1.1-.1.2 0 .4l1.6 1.6c.2.2.2.6 0 .8-.2.2-.6.2-.8 0l-1.6-1.6c-.2-.2-.4-.2-.6 0l-1.6 1.6c-.2.2-.6.2-.8 0-.2-.2-.2-.6 0-.8l1.6-1.6c.1-.1.1-.2 0-.4l-1.6-1.6c-.2-.2-.2-.6 0-.8.2-.2.6-.2.8 0l1.6 1.6c.2.2.4.2.6 0l1.6-1.6c.2-.2.6-.2.8 0z" fill="white"/>
            </svg>
            <div className="text-left">
              <p className="text-white/80 text-sm font-medium">Присъединете се към</p>
              <p className="text-white text-xl font-bold">нашата Viber група!</p>
            </div>
          </div>

          {/* Confetti decoration dots */}
          <div className="absolute top-4 left-8 w-2 h-2 bg-yellow-400 rounded-full opacity-70" />
          <div className="absolute top-12 right-12 w-1.5 h-1.5 bg-green-400 rounded-full opacity-70" />
          <div className="absolute top-20 left-16 w-1 h-3 bg-pink-400 rounded-sm opacity-60 rotate-45" />
          <div className="absolute top-8 right-20 w-1 h-3 bg-blue-300 rounded-sm opacity-60 -rotate-12" />

          {/* CarCube branding */}
          <div className="mb-5">
            <span className="text-3xl font-black">
              <span className="text-blue-300">Car</span>
              <span className="text-white">Cube</span>
            </span>
          </div>

          {/* CTA Button */}
          <a
            href={VIBER_INVITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block w-full max-w-xs bg-white text-purple-700 font-bold text-lg py-3.5 px-8 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 mb-5"
          >
            Присъедини се
          </a>

          {/* QR Code section */}
          <div className="flex items-center justify-center gap-3">
            <div className="bg-white rounded-lg p-2">
              <img
                src="/images/viber-qr.png"
                alt="Viber QR Code"
                className="w-20 h-20"
              />
            </div>
            <p className="text-white/70 text-sm text-left">
              или сканирай<br />QR кода
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ViberPopup;
